/**
 * A list of core API endpoints to be used by {@see IMobizHttpService}.
 */
import { IApiEndpointInformation } from '../interfaces/api-endpoint-information.interface';

export enum MobizServerApi {
  HealthCheck = 'health-check',
  AccountLogIn = 'oauth2/token',
  AccountLogOut = 'account/logout',
  RefreshToken = 'oauth2/token',
  AudkenniVerificationCode = 'e-cert-verification-code',
  ECertStartAppPolling = 'e-cert-start-app-polling',
  ECertStartSimPolling = 'e-cert-start-sim-polling',
  ECertPoll = 'e-cert-poll',

  Notifications = 'notifications/{{organizationId}}',
  NotificationsHub = 'notifications/hub',
  NotificationsSubscribe = 'notifications/subscribe',
  NotificationsUnsubscribe = 'notifications/unsubscribe',

  AccountConfirmEmail = 'account/email/confirm',
  AccountEmailRequestVerification = 'account/email/request-verification',
  AccountEmailVerificationStatus = 'account/email/verification/{{verificationId}}/status',
  AccountResendVerificationEmail = 'account/email/verification/{{verificationId}}/resend',
  AccountPhoneNumberRequestVerification = 'account/phone-number/request-verification',
  AccountPhoneNumberCheckVerificationToken = 'account/phone-number/verification/{{verificationId}}',
  AccountSignUp = 'account/sign-up',
  AccountRegister = 'account/user/register',
  AccountChangePassword = 'account/user/password',
  AccountResetPassword = 'account/user/reset-password',
  AccountForgotPassword = 'account/user/forgot-password',

  AllUsers = 'user',
  User = 'user/{{userId}}',
  UserToken = 'user/token/{{tokenId}}',
  UserTokens = 'user/{{userId}}/tokens',
  AddUserEmail = 'user/{{userId}}/email',
  RemoveUserEmail = 'user/{{userId}}/email/{{email}}',
  UserGroup = 'user/{{userId}}/group',

  AllAdminDomains = 'admin-domain',
  AdminDomain = 'admin-domain/{{adminDomainId}}',
  AllAdminDomainEndpoints = 'admin-domain/{{adminDomainId}}/endpoint',
  AdminDomainEndpoint = 'admin-domain/{{adminDomainId}}/endpoint/{{endpointId}}',
  AllAdminDomainMemberships = 'admin-domain/{{adminDomainId}}/membership',
  AdminDomainMembership = 'admin-domain/{{adminDomainId}}/membership/{{membershipId}}',

  Organization = 'organization/{{organizationId}}',
  AllOrganizations = 'organization',
  Membership = 'membership/{{membershipId}}',
  Invite = 'invite/{{id}}',
  InviteResend = 'invite/resend/{{id}}',

  AllSecurityPermissions = 'security/permission',
  AllSecurityRoles = 'security/role',
  SecurityRole = 'security/role/{{roleId}}',
  AllSecurityGroups = 'security/group',
  SecurityGroup = 'security/group/{{groupId}}',
  AllUsersFromSecurityGroup = 'security/group/{{groupId}}/user',

  ErpExternalSystemInfo = 'erp/info',
  ErpExternalSystemInfoByEndpoint = 'erp/info/endpoint/{{endpointId}}',
  ErpMembershipAuth = 'erp/authenticate/membership/{{membershipId}}',
  ErpOrganizationAuth = 'erp/authenticate/organization/{{organizationId}}',
  ErpAuthOnEndpoint = 'erp/authenticate/endpoint/{{endpointId}}',
  ErpOrganizationAuthOnEndpoint = 'erp/authenticate/organization/{{organizationId}}/endpoint/{{endpointId}}',
  ErpOrganizationStatus = 'erp/orgstatus/{{organizationId}}',
  ErpOrganizationBootstrap = 'erp/bootstrap/{{organizationId}}',
  ErpOrganizationSyncStatus = 'erp/syncstatus/{{organizationId}}',
  ErpAllUsers = 'erp/users/{{organizationId}}',
  ErpPeriod = 'erp/period/{{organizationId}}',
  ErpDim1 = 'erp/dim1/{{organizationId}}',
  ErpDim2 = 'erp/dim2/{{organizationId}}',
  ErpDim3 = 'erp/dim3/{{organizationId}}',

  // TODO: TP-9108 Move to reports sub-package.
  ReportRun = 'report/run/{{organizationId}}/{{reportName}}',
  ReportSetInfo = 'report/info/{{organizationId}}/{{reportSet}}',
  ReportMeta = 'report/meta/{{organizationId}}',
  ReportJsonSchema = 'report/json-schema/{{organizationId}}',

  Settings = 'settings/{{scope}}/{{scopeId}}/{{key}}',
  SystemSettingsAll = 'settings/system',
  SystemSettings = 'settings/system/{{key}}',

  ClientPluginInfo = 'extension/client-plugin/info',
  ClientPluginFile = 'extension/{{extensionKey}}/client-plugin/{{clientPluginKey}}/{{file}}',
  ClientPluginConfiguration = 'extension/client-plugin-configuration',
  ClientPluginConfigurationForMobizAdministrator = 'extension/client-plugin-configuration/mobiz-administrator',
  ClientPluginConfigurationResource = 'extension/client-plugin-configuration/{{configurationId}}',

  // TODO: TP-9108 Move to sales sub-package.
  SalesCustomerResource = 'sales/{{organizationId}}/customer/{{customerId}}',
  SalesCustomer = 'sales/{{organizationId}}/customer',
  SalesCustomerGroup = 'sales/{{organizationId}}/customer-group',
  SalesTax = 'sales/{{organizationId}}/tax',
  SalesTaxPercent = 'sales/{{organizationId}}/tax-percent',
  SalesCurrency = 'sales/{{organizationId}}/currency',
  SalesPaymentTerm = 'sales/{{organizationId}}/payment-term',
  SalesPaymentMethod = 'sales/{{organizationId}}/payment-method',
  SalesPriceGroup = 'sales/{{organizationId}}/price-group',
  SalesCountry = 'sales/{{organizationId}}/country',
  SalesPersons = 'sales/{{organizationId}}/sales-person',
  SalesItemResource = 'sales/{{organizationId}}/item/{{itemId}}',
  SalesItem = 'sales/{{organizationId}}/item',
  SalesItemGroup = 'sales/{{organizationId}}/item-group',
  SalesItemReceiver = 'sales/{{organizationId}}/item-receiver',
  SalesInvoice = 'sales/{{organizationId}}/invoice',
  SalesInvoiceResource = 'sales/{{organizationId}}/invoice/{{invoiceId}}',
  SalesInvoiceGenerateFromQuote = 'sales/{{organizationId}}/invoice/generate-from-quote',
  SalesInvoiceGenerateFromOrder = 'sales/{{organizationId}}/invoice/generate-from-order',
  SalesPostInvoiceResource = 'sales/{{organizationId}}/post/invoice/{{invoiceId}}',
  SalesPreviewInvoice = 'sales/{{organizationId}}/preview/invoice',
  SalesPreviewInvoiceResource = 'sales/{{organizationId}}/preview/invoice/{{fileId}}',
  SalesERPCalculateInvoiceLine = 'sales/{{organizationId}}/invoice/erp-calculate-line',
  SalesERPCalculateOrderLine = 'sales/{{organizationId}}/order/erp-calculate-line',
  SalesERPCalculateQuoteLine = 'sales/{{organizationId}}/quote/erp-calculate-line',
  SalesQuote = 'sales/{{organizationId}}/quote',
  SalesQuoteResource = 'sales/{{organizationId}}/quote/{{quoteId}}',
  SalesPreviewQuote = 'sales/{{organizationId}}/preview/quote',
  SalesPreviewQuoteResource = 'sales/{{organizationId}}/preview/quote/{{fileId}}',
  SalesOrder = 'sales/{{organizationId}}/order',
  SalesOrderResource = 'sales/{{organizationId}}/order/{{orderId}}',
  SalesOrderGenerateFromQuote = 'sales/{{organizationId}}/order/generate-from-quote',
  SalesPreviewOrder = 'sales/{{organizationId}}/preview/order',
  SalesPreviewOrderResource = 'sales/{{organizationId}}/preview/order/{{fileId}}',
  SalesWarehouse = 'sales/{{organizationId}}/warehouse',
  SalesSettingsTemplatePreview = 'sales/{{organizationId}}/preview-template',

  /** @deprecated use MobizCommonSandboxServerApi.ManagementRaiseException */
  ManagementRaiseException = 'management/exception/{{type}}',
}

/**
 * The OAuth endpoint errors aren't handled by GlobalApiExceptionFilter and don't respect
 * IMobizHttpApiErrorResponse.
 *
 * NOTE: This is not an IApiEndpointInformation field, because it's an issue that should be fixed.
 * TODO: Update the backend to always include a response body containing a predefined error object.
 *
 * @see mobiz-backend:Mobiz.Web.Core.ErrorHandling.GlobalApiExceptionFilter.OnException()
 * @see IMobizHttpApiErrorResponse
 *
 * @internal
 */
export function isOAuthApiEndpoint(serverApi: string): boolean {
  return serverApi === MobizServerApi.AccountLogIn || serverApi === MobizServerApi.RefreshToken;
}

/**
 * @internal This constant should never be used directly.
 */
export const API_ENDPOINT_INFORMATION_RECORDS_VALUE: Partial<Record<MobizServerApi, IApiEndpointInformation>> = {
  [MobizServerApi.AccountLogIn]: {
    noPrefix: true,
  },
};
